import React, { useState, useRef, useEffect } from 'react'
import { Group } from '@visx/group'
import { AreaStack } from '@visx/shape'
import { scaleLinear, scalePoint, scaleOrdinal } from '@visx/scale'
import { animated, useSpring, useTransition, useSprings } from '@react-spring/web'
import { localPoint } from '@visx/event'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { curveCardinal, curveLinear, curveMonotoneX } from '@visx/curve'

// Generic data interface
interface DataItem {
  category: string
  [key: string]: string | number
}

interface ChartLabel {
  key: string
  label: string
  tooltip?: string
}

interface ChartData {
  data: DataItem[]
  labels: ChartLabel[]
  colors: string[]
}

const defaultMargin = { top: 20, right: 40, bottom: 50, left: 50 }

export type StackedAreaChartProps = {
  width?: number
  height?: number
  margin?: typeof defaultMargin
  chartData: ChartData
  backgroundColor?: string
  tooltipUnit?: string
  xAxisLabel?: string
  yAxisLabel?: string
  animate?: boolean
  hideCategoryLabelsAfter?: number
  maxYValue?: number
  yAxisUnit?: string
  curve?: 'linear' | 'cardinal' | 'monotone'
}

type TooltipData = {
  category: string
  values: Array<{
    key: string
    label: string
    value: number
    color: string
  }>
  x: number
  y: number
}

const getCurveFunction = (curve: string) => {
  switch (curve) {
    case 'cardinal':
      return curveCardinal
    case 'monotone':
      return curveMonotoneX
    default:
      return curveLinear
  }
}

export function StackedAreaChart({
  width,
  height,
  margin = defaultMargin,
  chartData,
  backgroundColor = '#FFF',
  tooltipUnit = '',
  xAxisLabel = 'Categories',
  yAxisLabel = 'Values',
  animate = true,
  hideCategoryLabelsAfter = 10,
  maxYValue,
  yAxisUnit = '',
  curve = 'linear',
}: StackedAreaChartProps) {
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null)
  const [mouseX, setMouseX] = useState<number | null>(null)
  const [hoveredArea, setHoveredArea] = useState<string | null>(null)
  const [isMouseInChart, setIsMouseInChart] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({
    width: width || 500,
    height: height || 400,
  })

  // Responsive sizing effect
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver((entries) => {
      const { width: containerWidth } = entries[0].contentRect
      if (containerWidth > 0) {
        const aspectRatio = 1.6 // 16:10 aspect ratio
        const newHeight = containerWidth / aspectRatio
        setDimensions({
          width: width || containerWidth,
          height: height || newHeight,
        })
      }
    })

    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [width, height])

  if (dimensions.width < 10) return null

  const { data, labels, colors } = chartData

  // Extract key names for calculations
  const keyNames = labels.map((k) => k.key)

  // Check if we have only one data point
  const isSingleDataPoint = data.length === 1

  // Calculate totals for each category (for maxYValue fallback)
  const totals = data.map((d) => {
    return keyNames.reduce((sum, key) => sum + (Number(d[key]) || 0), 0)
  })

  // Bounds
  const innerWidth = dimensions.width - margin.left - margin.right
  const innerHeight = dimensions.height - margin.top - margin.bottom

  // Scales
  const xScale = scalePoint<string>({
    domain: data.map((d) => d.category),
    range: [0, innerWidth],
    padding: 0, // Remove padding to extend areas to full width
  })

  const yScale = scaleLinear<number>({
    domain: [0, maxYValue !== undefined ? maxYValue : Math.max(...totals)],
    range: [innerHeight, 0],
    nice: true,
  })

  const colorScale = scaleOrdinal<string, string>({
    domain: keyNames,
    range: colors,
  })

  // Animation for the axes and grid
  const axisAnimation = useSpring({
    from: animate ? { opacity: 0 } : { opacity: 1 },
    to: { opacity: 1 },
    config: { tension: 300, friction: 20 },
  })

  // Animation for the legend
  const legendTransition = useTransition(labels, {
    from: animate ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 },
    enter: { opacity: 1, y: 0 },
    trail: 100, // Stagger the animations
    config: { tension: 300, friction: 20 },
  })

  // Area animations - each area animates in sequence
  const areaAnimations = useSprings(
    keyNames.length,
    keyNames.map((_, keyIndex) => ({
      from: animate ? { opacity: 0, scaleY: 0 } : { opacity: 1, scaleY: 1 },
      to: { opacity: 1, scaleY: 1 },
      delay: animate ? keyIndex * 200 : 0,
      config: { tension: 170, friction: 26 },
    }))
  )

  // Get formatted data for display
  const getFormattedValue = (value: number) => {
    return new Intl.NumberFormat().format(value)
  }

  // Check if we should show category labels
  const showCategoryLabels = data.length <= hideCategoryLabelsAfter

  return (
    <>
      <div
        ref={containerRef}
        className="relative w-full"
        onMouseEnter={() => setIsMouseInChart(true)}
        onMouseMove={(event) => {
          const bounds = event.currentTarget.getBoundingClientRect()
          const x = event.clientX - bounds.left - margin.left
          if (x >= 0 && x <= innerWidth) {
            setMouseX(x)
          }
        }}
        onMouseLeave={() => {
          setIsMouseInChart(false)
          setMouseX(null)
          setTooltipData(null)
          setHoveredArea(null)
        }}
      >
        <svg width={dimensions.width} height={dimensions.height}>
          <rect rx={14} width={dimensions.width} height={dimensions.height} fill={backgroundColor} />
          <Group top={margin.top} left={margin.left}>
            {/* Vertical hover line */}
            {mouseX !== null && (
              <line x1={mouseX} x2={mouseX} y1={0} y2={innerHeight} stroke="#D7D7D7" strokeWidth={1} opacity={1} />
            )}

            {/* Y-axis label with animation */}
            <animated.text
              x={-innerHeight / 2}
              y={-35}
              transform="rotate(-90)"
              textAnchor="middle"
              className="text-secondary"
              fontSize={12}
              style={{ opacity: axisAnimation.opacity }}
            >
              {yAxisLabel}
            </animated.text>

            {/* Grid with animation */}
            <animated.g style={{ opacity: axisAnimation.opacity }}>
              {/* Horizontal grid lines only at tick positions */}
              {yScale.ticks(6).map((tickValue) => {
                const yPos = yScale(tickValue)
                return (
                  <line
                    key={`grid-horizontal-${tickValue}`}
                    x1={0}
                    x2={innerWidth}
                    y1={yPos}
                    y2={yPos}
                    stroke="#D1D5DB"
                    strokeDasharray="3,3"
                    strokeOpacity={0.8}
                    strokeWidth={1}
                  />
                )
              })}

              {/* Vertical grid lines for each category/day - skip for single data point */}
              {!isSingleDataPoint &&
                data.map((d) => {
                  const xPos = xScale(d.category)
                  if (xPos === undefined) return null

                  return (
                    <line
                      key={`grid-vertical-${d.category}`}
                      x1={xPos}
                      x2={xPos}
                      y1={0}
                      y2={innerHeight}
                      stroke="#D1D5DB"
                      strokeDasharray="3,3"
                      strokeOpacity={0.8}
                      strokeWidth={1}
                    />
                  )
                })}
            </animated.g>

            {/* Conditional rendering: bars for single data point, areas for multiple */}
            {isSingleDataPoint ? (
              /* Stacked Bars for single data point */
              (() => {
                const d = data[0]
                const xPos = xScale(d.category) || 0
                const barWidth = Math.min(innerWidth * 0.4, 120) // 40% of width, max 120px
                let runningTotal = 0

                return keyNames
                  .slice()
                  .reverse()
                  .map((key, reverseIndex) => {
                    const originalIndex = keyNames.length - 1 - reverseIndex
                    const value = Number(d[key]) || 0
                    const barHeight = yScale(runningTotal) - yScale(runningTotal + value)
                    const yPos = yScale(runningTotal + value)
                    const isHovered = hoveredArea === key
                    const barOpacity = isMouseInChart ? (isHovered ? 1 : 0.3) : 1

                    runningTotal += value

                    return (
                      <animated.rect
                        key={`bar-${key}`}
                        x={xPos - barWidth / 2}
                        y={yPos}
                        width={barWidth}
                        height={barHeight}
                        fill={colorScale(key)}
                        fillOpacity={0.2}
                        stroke={colorScale(key)}
                        strokeWidth={2}
                        opacity={barOpacity}
                        rx={4} // Rounded corners
                        className="transition-opacity duration-200"
                        style={{
                          opacity:
                            areaAnimations[originalIndex]?.opacity.to((o: number) => o * barOpacity) || barOpacity,
                          transformOrigin: 'bottom',
                          transform:
                            areaAnimations[originalIndex]?.scaleY.to((scale: number) => `scaleY(${scale})`) ||
                            'scaleY(1)',
                        }}
                      />
                    )
                  })
              })()
            ) : (
              /* Stacked Areas for multiple data points */
              <AreaStack
                data={data}
                keys={keyNames}
                x={(d) => xScale(d.data.category) || 0}
                y0={(d) => yScale(d[0])}
                y1={(d) => yScale(d[1])}
                curve={getCurveFunction(curve)}
              >
                {({ stacks, path }) => (
                  <>
                    {stacks
                      .slice()
                      .reverse()
                      .map((stack, reverseIndex) => {
                        const originalIndex = stacks.length - 1 - reverseIndex
                        const areaKey = keyNames[originalIndex]
                        const isHovered = hoveredArea === areaKey
                        const areaOpacity = isMouseInChart ? (isHovered ? 1 : 0.3) : 1

                        return (
                          <animated.path
                            key={`area-${areaKey}`}
                            d={path(stack) || ''}
                            fill={colorScale(areaKey)}
                            fillOpacity={0.2}
                            stroke={colorScale(areaKey)}
                            strokeWidth={2}
                            strokeOpacity={1.0}
                            opacity={areaOpacity}
                            className="transition-opacity duration-200"
                            style={{
                              opacity:
                                areaAnimations[originalIndex]?.opacity.to((o: number) => o * areaOpacity) ||
                                areaOpacity,
                              transformOrigin: 'bottom',
                              transform:
                                areaAnimations[originalIndex]?.scaleY.to((scale: number) => `scaleY(${scale})`) ||
                                'scaleY(1)',
                            }}
                          />
                        )
                      })}
                  </>
                )}
              </AreaStack>
            )}

            {/* Hover detection overlay - visible for debugging */}
            {data.map((d, i) => {
              const xPos = xScale(d.category) || 0
              // For single data point, use the actual bar width
              // For multiple points, extend to neighboring points for seamless hover
              const hoverWidth = isSingleDataPoint
                ? Math.min(innerWidth * 0.4, 120) // Same width as the bar for single point
                : i === 0 || i === data.length - 1
                  ? innerWidth / data.length // First and last points
                  : (xScale(data[Math.min(i + 1, data.length - 1)].category) || innerWidth) -
                    (xScale(data[Math.max(i - 1, 0)].category) || 0) // Middle points span to neighbors

              const hoverX = isSingleDataPoint
                ? xPos - hoverWidth / 2
                : i === 0
                  ? 0 // Start from left edge
                  : (xScale(data[i - 1].category) || 0) + (xPos - (xScale(data[i - 1].category) || 0)) / 2

              return (
                <rect
                  key={`hover-area-${i}`}
                  x={hoverX}
                  y={0}
                  width={i === data.length - 1 && !isSingleDataPoint ? innerWidth - hoverX : hoverWidth}
                  height={innerHeight}
                  fill="transparent"
                  onMouseMove={(event) => {
                    const point = localPoint(event) || { x: 0, y: 0 }
                    setMouseX(xPos)

                    // Get all values for this category
                    const values = labels.map((chartLabel) => ({
                      key: chartLabel.key,
                      label: chartLabel.label,
                      value: Number(d[chartLabel.key]) || 0,
                      color: colorScale(chartLabel.key),
                    }))

                    // Determine which area the mouse is over based on Y position
                    const yPos = point.y
                    let runningTotal = 0
                    let hoveredKey: string | null = null

                    // Build stack values to find which area contains the mouse Y position
                    for (let j = 0; j < keyNames.length; j++) {
                      const value = Number(d[keyNames[j]]) || 0
                      const bottomY = yScale(runningTotal)
                      const topY = yScale(runningTotal + value)

                      if (yPos >= topY && yPos <= bottomY) {
                        hoveredKey = keyNames[j]
                        break
                      }
                      runningTotal += value
                    }

                    setHoveredArea(hoveredKey)
                    setTooltipData({
                      category: d.category,
                      values,
                      x: point.x,
                      y: point.y,
                    })
                  }}
                  onMouseLeave={() => {
                    setMouseX(null)
                    setTooltipData(null)
                    setHoveredArea(null)
                  }}
                />
              )
            })}

            {/* Axes with animation */}
            <animated.g style={{ opacity: axisAnimation.opacity }}>
              <AxisLeft
                scale={yScale}
                stroke="#888888"
                tickStroke="#888888"
                numTicks={6}
                tickLabelProps={{
                  className: 'text-secondary font-normal text-xs',
                  textAnchor: 'end',
                  dy: '0.33em',
                }}
                tickFormat={(value) => `${value}${yAxisUnit}`}
              />
              <AxisBottom
                top={innerHeight}
                scale={xScale}
                stroke="#888888"
                tickStroke="#888888"
                tickLabelProps={() => ({
                  className: 'text-secondary font-normal text-xs',
                  textAnchor: 'middle',
                  dy: '0.33em',
                  opacity: showCategoryLabels ? 1 : 0,
                })}
              />
            </animated.g>

            {/* X-axis label with animation */}
            <animated.text
              x={innerWidth / 2}
              y={innerHeight + 45}
              textAnchor="middle"
              className="text-primary font-normal text-xs"
              style={{ opacity: axisAnimation.opacity }}
            >
              {xAxisLabel}
            </animated.text>
          </Group>
        </svg>

        {/* Tooltip */}
        {tooltipData && (
          <div
            className="absolute bg-primary text-primary shadow-md rounded p-2 pointer-events-none z-10 text-sm"
            style={{
              top: tooltipData.y,
              left: tooltipData.x + 15,
              transform: 'translateY(-50%)',
              border: '1px solid #e0e0e0',
              whiteSpace: 'nowrap',
            }}
          >
            <div className="text-primary text-sm mb-1 font-semibold">{tooltipData.category}</div>
            {tooltipData.values.map(({ key, label, value, color }) => (
              <div key={key} className="flex items-center gap-4 text-secondary">
                <div
                  className="w-4 h-4 shrink-0 rounded-sm"
                  style={{
                    backgroundColor: color,
                  }}
                />
                <span className="flex-1 text-primary text-sm font-normal">{label}</span>
                <span className="text-right tabular-nums text-primary text-sm font-normal ml-8">
                  {getFormattedValue(value)}
                  {tooltipUnit && `${tooltipUnit}`}
                </span>
              </div>
            ))}
            {/* Arrow pointing left */}
            <div
              className="absolute w-0 h-0 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-white"
              style={{
                top: '50%',
                left: '-8px',
                marginTop: '-8px',
              }}
            />
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-row flex-wrap gap-4 pt-1 justify-center">
        {legendTransition((style, chartLabel, _, index) => {
          return (
            <animated.div key={chartLabel.key} className="flex items-center gap-2 text-lg" style={style}>
              <div
                className="w-4 h-4 shrink-0 rounded-sm"
                style={{
                  backgroundColor: colors[index],
                }}
              />
              <span
                className="text-xs text-primary font-normal"
                title={chartLabel.tooltip} // Native HTML tooltip
              >
                {chartLabel.label}
              </span>
            </animated.div>
          )
        })}
      </div>
    </>
  )
}
